# Exporting candidate profiles

The SEEK API lets you retrieve information about a candidate and their profile related to a particular application.

When a candidate applies for a job ad they consent to a snapshot of their SEEK Profile being shared with the hirer.
SEEK recommends that any retrieved candidate information only be available for viewing under the job ad that the application was related to.
If you decide to persist a general candidate profile in your software, it must be scoped to the SEEK hirer that received the application.

## Before you begin

Candidate profile exports require a [partner token](/auth/partner-tokens).

## Data structure

```mermaid Candidate application profile objects
erDiagram
        CandidateProfile }|--|| Candidate : "belongs to"
        CandidateProfile ||--|| PositionOpening : "is associated with"
        CandidateProfile ||--|| PositionProfile : "is associated with"
        PositionOpening ||--|{ PositionProfile : contains
```

The [`CandidateProfile`] object links to:

- The candidate that the profile belongs to via the [`CandidateProfile.candidate`] field.
  - [`Candidate.person`] contains the candidate’s name, home location, email address, and phone number.

    This field is connected to the candidate’s live SEEK Profile;
    the query response will reflect updates made by the candidate after applying.

  - [`Candidate.documentId`] can be used as a stable identifier for the candidate across multiple applications.

- The [job ad] that the candidate is applying to via the [`CandidateProfile.associatedPositionProfile`] field.

  You can use this to associate the candidate application with a SEEK job ad and hirer.

  The [position opening] associated with the job ad can be retrieved via the [`PositionProfile.positionOpening`] field, if needed.

The candidate profile itself contains:

- Structured details of the candidate’s employment history, education, and more.

  These fields are taken as a snapshot of the candidate’s SEEK Profile when they apply through the SEEK website or app.

- Links to [attachments] such as the candidate’s resume and cover letter.

- The [candidate’s responses] to the job’s screening questions via a `seekQuestionnaireSubmission` field.
  This will only be present if a [questionnaire] was provided when posting the job ad.

For detailed information on all available fields, refer to the [`CandidateProfile` schema definition](/schema/named-type/CandidateProfile).
Only a name and email address are required to create a SEEK candidate account;
all other fields in a candidate’s profile are optional.

SEEK may redact fields and omit [PII] when a candidate or application is deleted.
This includes attachments and questionnaire submissions.

The SEEK API does not translate user input and will serve SEEK Profile information as supplied by the candidate.
As a result, your software may need to handle multilingual candidate data.

[pii]: https://en.wikipedia.org/wiki/Personal_data
[questionnaire]: /use-cases/job-posting/questionnaires

## Operations

Use the [GraphQL `candidateProfile` query] to retrieve candidate profiles.
You should combine this with the query you use to retrieve [attachments] and [questionnaire submissions].

### candidateProfile

```graphql
query ($id: String!) {
  candidateProfile(id: $id) {
    profileId {
      value
    }
    createDateTime
    candidate {
      person {
        name {
          given
          family
        }
        communication {
          address {
            # Select other address fields as necessary
            formattedAddress
            countryCode
          }
          phone {
            formattedNumber
          }
          email {
            address
          }
        }
      }
    }
    associatedPositionProfile {
      seekHirerJobReference
      positionOrganizations {
        id {
          value
        }
        name
      }
      positionUri
    }
    employment {
      organization {
        name
      }
      positionHistories {
        start
        end
        title
      }
    }
    education {
      institution {
        name
      }
      educationDegrees {
        name
        degreeGrantedStatus
        date
      }
      descriptions
    }
    certifications {
      name
      issued
      effectiveTimePeriod {
        validTo
      }
      issuingAuthority {
        name
      }
      descriptions
    }
    qualifications {
      competencyName
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:candidateProfile:apply:5G5NaFDmy7UigvGy6aT6VT"
}
```

```jsonc
{
  "candidateProfile": {
    "profileId": {
      "value": "seekAnzPublicTest:candidateProfile:apply:5G5NaFDmy7UigvGy6aT6VT"
    },
    "createDateTime": "2019-09-03T13:50:23.223Z",
    "candidate": {
      "person": {
        "name": {
          "given": "Ella",
          "family": "Greenholt"
        },
        "communication": {
          "address": [
            {
              "formattedAddress": "Coober Pedy SA 5723 AU",
              "countryCode": "AU"
            }
          ],
          "phone": [
            {
              "formattedNumber": "(02) 5550 5678"
            }
          ],
          "email": [
            {
              "address": "EllaG@gmail.com"
            }
          ]
        }
      }
    },
    "associatedPositionProfile": {
      "seekHirerJobReference": "12b5d6bf-4723-4ee9-bdc0-e530ccddd7b7",
      // The hirer associated with the job ad
      "positionOrganizations": [
        {
          "id": {
            "value": "seekAnzPublicTest:organization:seek:93WyyF1h"
          },
          "name": "Acme Corp"
        }
      ],
      "positionUri": "https://au.candidate.example.seek.com/job/30000001"
    },
    // `Career history` from the candidate’s SEEK Profile
    "employment": [
      {
        "organization": {
          "name": "Swift Inc"
        },
        "positionHistories": [
          {
            "start": "2015-05",
            "end": "2018-03",
            "title": "Customer Service Representative"
          }
        ]
      },
      {
        "organization": {
          "name": "Ledner Inc"
        },
        "positionHistories": [
          {
            "start": "2018-10",
            "end": null,
            "title": "Customer Service Manager"
          }
        ]
      }
    ],
    // `Education` from the candidate’s SEEK Profile
    "education": [
      {
        "institution": {
          "name": "Adelaide Business School"
        },
        "educationDegrees": [
          {
            "name": "Bachelor of Business Administration",
            "degreeGrantedStatus": "Granted",
            "date": "2016-10"
          }
        ],
        "descriptions": []
      },
      {
        "institution": {
          "name": "University of New South Wales"
        },
        "educationDegrees": [
          {
            "name": "Masters of Business Administration",
            "degreeGrantedStatus": "Granted",
            "date": "2018-07"
          }
        ],
        "descriptions": ["3.8 GPA"]
      }
    ],
    // `Licences & certifications` from the candidate’s SEEK Profile
    "certifications": [
      {
        "name": "SA Drivers Licence",
        "issued": null,
        "effectiveTimePeriod": {
          "validTo": "2021-07"
        },
        "issuingAuthority": null,
        "descriptions": []
      },
      {
        "name": "Criminal History Check",
        "issued": "2021-01",
        "effectiveTimePeriod": {
          "validTo": "2023-01"
        },
        "issuingAuthority": null,
        "descriptions": ["Police check passed with no criminal history."]
      },
      {
        "name": "Working with Children Check",
        "issued": null,
        "effectiveTimePeriod": {
          "validTo": null
        },
        "issuingAuthority": {
          "name": "Government of South Australia"
        },
        "descriptions": []
      }
    ],
    // `Skills` from the candidate’s SEEK Profile
    "qualifications": [
      {
        "competencyName": "Team building"
      },
      {
        "competencyName": "Customer focus"
      },
      {
        "competencyName": "Stakeholder management"
      }
    ]
  }
}
```

[`candidate.documentid`]: /schema/named-type/Candidate#/field/documentId
[`candidate.person`]: /schema/named-type/Candidate#/field/person
[`candidateprofile`]: /schema/named-type/CandidateProfile
[`candidateprofile.associatedpositionprofile`]: /schema/named-type/CandidateProfile#/field/associatedPositionProfile
[`candidateprofile.candidate`]: /schema/named-type/CandidateProfile#/field/candidate
[`positionprofile.positionopening`]: /schema/named-type/PositionProfile#/field/positionOpening
[attachments]: /use-cases/application-export/attachments
[candidate’s responses]: /use-cases/application-export/questionnaire-submissions
[graphql `candidateprofile` query]: /schema/query/candidateProfile
[job ad]: /use-cases/job-posting/managing-job-ads
[position opening]: /use-cases/job-posting/position-openings
[questionnaire submissions]: /use-cases/application-export/questionnaire-submissions
